/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import {
    OrgUnitAvailability,
    OrgUnitAvailabilitySearchCriteria,
    ProductType,
    ProductWarehouseInventoryAvailability,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import { searchProductAvailabilityAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/OrgUnitsDataActions.g';

import {
    getProductAvailabilitiesForSelectedVariantAction,
    ProductAvailabilitiesForSelectedVariantInput
} from './get-product-availabilities-for-selected-variant';
import getSelectedVariant, { SelectedVariantInput } from './get-selected-variant';
import { ArrayExtensions, getSelectedProductIdFromActionInput } from './utilities';
import { isCrossCompanyInventoryLookupEcommerceEnabled } from './utilities/cross-entity-inventory-feature';
import { mapOrgUnitAvailabilityToCrossEntityNode } from './utilities/cross-entity-inventory-mappers';
import { ICrossEntityInventoryNode } from './utilities/cross-entity-inventory-types';
import { DeliveryMode, IProductInventoryInformation } from './utilities/product-inventory-information';
import { isAggregatedInventory, mapProductInventoryInformation } from './utilities/product-inventory-utils';

/**
 * Result limit once the lookup is scoped to a designated warehouse. The server-side filter
 * narrows the result to that warehouse plus any substring collisions. Keep the standard page
 * size because older HQ versions can ignore the criteria and return an unfiltered result.
 */
const DESIGNATED_WAREHOUSE_RESULT_LIMIT = 250;

/**
 * Input for the getProductAvailabilityDispatcher data action.
 * Chooses the Old-IV or New-IV (Cross-LE) PDP availability path.
 */
export class ProductAvailabilityDispatcherInput implements IActionInput {
    public productId: number;

    public channelId: number;

    public selectedProduct?: SimpleProduct;

    public variantId?: number;

    public dataAreaIds?: string[];

    /**
     * Designated warehouse number (B2B named warehouse, S15).
     * Optional explicit override that scopes the lookup to a specific STORE. Note that CRT treats
     * OrgUnitNumber as a store number and excludes all non-store warehouses when it is supplied,
     * so it must not be combined with the default-warehouse scoping below.
     */
    public orgUnitNumber?: string;

    /**
     * Designated warehouse identifier. The value must be an INVENTLOCATIONID, not a display name.
     * When omitted, the New-IV path falls back to the channel's default warehouse
     * (ChannelConfiguration.InventLocation).
     */
    public orgUnitName?: string;

    public constructor(
        productId: number | string,
        channelId: number,
        options?: {
            selectedProduct?: SimpleProduct;
            variantId?: number;
            dataAreaIds?: string[];
            orgUnitNumber?: string;
            orgUnitName?: string;
        }
    ) {
        this.productId = typeof productId === 'string' ? +productId : productId;
        this.channelId = channelId;
        this.selectedProduct = options?.selectedProduct;
        this.variantId = options?.variantId;
        this.dataAreaIds = options?.dataAreaIds;
        this.orgUnitNumber = options?.orgUnitNumber;
        this.orgUnitName = options?.orgUnitName;
    }

    public getCacheKey = (): string => 'ProductAvailabilityDispatcher';

    public getCacheObjectType = (): string => 'ProductAvailabilities';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the getProductAvailabilityDispatcher data action.
 * @param inputData - The create-action context.
 * @returns The input instance.
 */
export const createProductAvailabilityDispatcherInput = (
    inputData: ICreateActionContext<IGeneric<IAny>>
): ProductAvailabilityDispatcherInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);
    if (productId) {
        return new ProductAvailabilityDispatcherInput(+productId, +inputData.requestContext.apiSettings.channelId);
    }
    throw new Error('Unable to create ProductAvailabilityDispatcherInput, no productId found on module config or query');
};

/**
 * Maps a Cross-LE node to the legacy ProductWarehouseInventoryAvailability shape so the New-IV
 * (SearchProductAvailability) quantities can be processed by the existing inventory mapping utilities.
 * Note: New-IV returns only raw quantities (no RS-computed inventory level codes/labels or max-purchasable
 * quantities), so buffer-based behavior may differ from the Old-IV path and stock labels are derived
 * client-side downstream. Field mapping per the New-IV contract:
 *   TotalAvailable    = AvailableQuantity              (equivalent to Old-IV TotalAvailable)
 *   PhysicalAvailable = AvailableQuantity - OrderedSum (equivalent to Old-IV PhysicalAvailable)
 * @param node - The designated-warehouse Cross-LE node.
 * @param productId - The resolved product id.
 * @returns A legacy-compatible ProductWarehouseInventoryAvailability.
 */
export const mapCrossEntityNodeToWarehouseInventoryAvailability = (
    node: ICrossEntityInventoryNode,
    productId: number
): ProductWarehouseInventoryAvailability => {
    const totalAvailable = node.availableQuantity ?? 0;
    const physicalAvailable = Math.max(0, totalAvailable - (node.orderedSum ?? 0));
    return {
        ProductId: productId,
        InventLocationId: node.inventoryLocationId,
        TotalAvailable: totalAvailable,
        PhysicalAvailable: physicalAvailable
    };
};

/**
 * Selects the designated warehouse node from the New-IV result.
 * (legacy-compatible single-node behavior).
 * @param nodes - The mapped Cross-LE nodes.
 * @param criteria - Optional warehouse identity to prefer.
 * @returns The designated node, or undefined when there are no nodes.
 */
export const selectDesignatedNode = (
    nodes: ICrossEntityInventoryNode[],
    criteria?: { orgUnitNumber?: string; dataAreaId?: string; inventoryLocationId?: string }
): ICrossEntityInventoryNode | undefined => {
    if (!ArrayExtensions.hasElements(nodes)) {
        return undefined;
    }

    if (criteria && (criteria.orgUnitNumber || criteria.dataAreaId || criteria.inventoryLocationId)) {
        return nodes.find(
            node =>
                (criteria.orgUnitNumber === undefined || node.orgUnitNumber?.toLowerCase() === criteria.orgUnitNumber.toLowerCase()) &&
                (criteria.dataAreaId === undefined || node.dataAreaId?.toLowerCase() === criteria.dataAreaId.toLowerCase()) &&
                (criteria.inventoryLocationId === undefined ||
                    node.inventoryLocationId?.toLowerCase() === criteria.inventoryLocationId.toLowerCase())
        );
    }

    return nodes[0];
};

/**
 * Builds the SearchProductAvailability scoping criteria for the designated warehouse.
 *
 * CRT matches OrgUnitAvailabilitySearchCriteria.OrgUnitName against the warehouse
 * INVENTLOCATIONID (SearchWarehousesDataRequest, LIKE '%text%' within the channel's fulfillment
 * group), which is what allows a pure DC/warehouse that is not itself an org unit to be returned.
 *
 * OrgUnitNumber is intentionally never combined with it: CRT treats OrgUnitNumber as a store
 * number and empties the warehouse collection entirely when it is present, so the default
 * warehouse could never come back. It is honored only as an explicit store-scoped override.
 * @param input - The dispatcher input.
 * @param channelDefaultInventoryLocationId - The channel's default warehouse, when known.
 * @param channelDefaultInventoryLocationDataAreaId - The channel default warehouse's legal entity.
 * @returns The search criteria and the warehouse identity used for scoping.
 */
const buildDesignatedWarehouseCriteria = (
    input: ProductAvailabilityDispatcherInput,
    channelDefaultInventoryLocationId?: string,
    channelDefaultInventoryLocationDataAreaId?: string
): { searchCriteria: OrgUnitAvailabilitySearchCriteria; scopedWarehouseId?: string } => {
    const searchCriteria: OrgUnitAvailabilitySearchCriteria = {};
    let scopedWarehouseId: string | undefined;

    if (input.orgUnitNumber) {
        // Explicit store-scoped override. Warehouses are excluded server-side by design.
        searchCriteria.OrgUnitNumber = input.orgUnitNumber;
    } else {
        scopedWarehouseId = input.orgUnitName ?? channelDefaultInventoryLocationId;
        if (scopedWarehouseId) {
            searchCriteria.OrgUnitName = scopedWarehouseId;
        }
    }

    if (ArrayExtensions.hasElements(input.dataAreaIds)) {
        searchCriteria.DataAreaIds = input.dataAreaIds;
    } else if (channelDefaultInventoryLocationDataAreaId) {
        searchCriteria.DataAreaIds = [channelDefaultInventoryLocationDataAreaId];
    }

    return { searchCriteria, scopedWarehouseId };
};

/**
 * New-IV PDP availability: fetches the designated-warehouse availability via SearchProductAvailability
 * and maps it to a legacy-compatible single-node result.
 * @param input - The dispatcher input.
 * @param ctx - The action context.
 * @returns Legacy-compatible inventory information, or undefined.
 */
export async function getDesignatedWarehouseAvailability(
    input: ProductAvailabilityDispatcherInput,
    ctx: IActionContext
): Promise<IProductInventoryInformation[] | undefined> {
    const productId = input.variantId ?? input.selectedProduct?.RecordId ?? input.productId;
    if (!productId) {
        return undefined;
    }

    // Parity with the Old-IV path: no status when stock check is disabled.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Reading global app config.
    if (!ctx.requestContext.app.config.enableStockCheck) {
        return undefined;
    }

    // Site setting "Inventory level for multiple warehouses" = Aggregated is not supported by the
    // New-IV SearchProductAvailability API yet (it has no aggregated option). The dispatcher routes
    // aggregated configurations to the Old-IV path (which supports them); this remains a defensive
    // guard for any direct caller so it never returns a single-warehouse figure as if aggregated.
    // TODO (Cross-LE Phase 2): implement aggregated inventory for the New-IV path once supported.
    if (isAggregatedInventory(ctx)) {
        ctx.telemetry.debug('[GetProductAvailabilityDispatcher] aggregated inventory not supported on New-IV path; skipping');
        return undefined;
    }

    try {
        // The designated/default warehouse is a CHANNEL concept (ChannelConfiguration.InventLocation),
        // not a per-user value, so it is read from the request context — no extra round trip.
        const channelDefaultInventoryLocationId = ctx.requestContext.channel?.InventLocation;
        const channelDefaultInventoryLocationDataAreaId = ctx.requestContext.channel?.InventLocationDataAreaId;

        const { searchCriteria, scopedWarehouseId } = buildDesignatedWarehouseCriteria(
            input,
            channelDefaultInventoryLocationId,
            channelDefaultInventoryLocationDataAreaId
        );

        if (!scopedWarehouseId && !input.orgUnitNumber) {
            ctx.telemetry.warning(
                '[GetProductAvailabilityDispatcher] no designated warehouse is configured; skipping PDP availability lookup'
            );
            return undefined;
        }

        const availabilities: OrgUnitAvailability[] = await searchProductAvailabilityAsync(
            {
                callerContext: ctx,
                queryResultSettings: {
                    Paging: { Top: DESIGNATED_WAREHOUSE_RESULT_LIMIT }
                }
            },
            productId,
            searchCriteria
        );

        const nodes = (availabilities ?? []).map(availability => mapOrgUnitAvailabilityToCrossEntityNode(availability, productId));

        // The server-side OrgUnitName filter is a substring (LIKE '%text%') match, and older HQ
        // versions can ignore the criteria entirely. Re-assert an exact match client-side.
        const designated = selectDesignatedNode(nodes, {
            orgUnitNumber: input.orgUnitNumber,
            inventoryLocationId: input.orgUnitNumber ? undefined : scopedWarehouseId
        });

        if (!designated) {
            ctx.telemetry.warning(
                '[GetProductAvailabilityDispatcher] designated warehouse was not returned exactly; skipping PDP availability'
            );
            return undefined;
        }

        // Reuse the exact Old-IV quantity/threshold logic (honors inventoryLevel, outOfStockThreshold,
        // enableStockCheck) by running the mapped node through mapProductInventoryInformation.
        const warehouseInventory = mapCrossEntityNodeToWarehouseInventoryAvailability(designated, productId);
        const inventoryInformation = mapProductInventoryInformation(ctx, [warehouseInventory]);
        if (!ArrayExtensions.hasElements(inventoryInformation)) {
            return undefined;
        }
        inventoryInformation[0].deliveryType = DeliveryMode.shipping;
        // Carry the raw available quantity so the buybox can derive the two-threshold stock label
        // client-side without double-applying the out-of-stock threshold.
        inventoryInformation[0].CrossEntityAvailableQuantity = designated.availableQuantity ?? 0;
        inventoryInformation[0].CrossEntityOrderedQuantity = designated.orderedSum ?? 0;
        // Carry the designated warehouse identity so the B2B PDP can surface the named warehouse.
        inventoryInformation[0].OrgUnitName = designated.orgUnitName;
        inventoryInformation[0].OrgUnitNumber = designated.orgUnitNumber;
        return inventoryInformation;
    } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- telemetry.exception accepts unknown.
        ctx.telemetry.exception(error);
        ctx.telemetry.debug('[GetProductAvailabilityDispatcher] error getting New-IV designated-warehouse availability');
        return undefined;
    }
}

/**
 * Action method for the getProductAvailabilityDispatcher data action.
 * Reads the Site Builder app configuration, then delegates to the New-IV (Cross-LE) or Old-IV path.
 * @param input - The dispatcher input.
 * @param ctx - The action context.
 * @returns Legacy-compatible inventory information, or undefined.
 */
export async function getProductAvailabilityDispatcherAction(
    input: ProductAvailabilityDispatcherInput,
    ctx: IActionContext
): Promise<IProductInventoryInformation[] | undefined> {
    // The Old-IV action supports aggregated inventory; the New-IV (SearchProductAvailability) API does
    // not, so route to whichever path can serve the current site configuration.
    const useOldIvPath = (): Promise<IProductInventoryInformation[] | undefined> =>
        getProductAvailabilitiesForSelectedVariantAction(
            new ProductAvailabilitiesForSelectedVariantInput(input.productId, input.channelId, input.selectedProduct),
            ctx
        );

    if (isCrossCompanyInventoryLookupEcommerceEnabled(ctx.requestContext.app.config)) {
        // New-IV has no aggregated-inventory option. When the site is configured for aggregated
        // inventory (inventoryLevel totalAvailable/physicalAvailable + warehouseAggregation multiple),
        // fall back to the Old-IV action so availability is not lost — otherwise the New-IV path returns
        // undefined and productAvailableQuantity disappears (a regression vs feature-off).
        if (isAggregatedInventory(ctx)) {
            ctx.telemetry.debug('[GetProductAvailabilityDispatcher] aggregated inventory: falling back to Old-IV path');
            return useOldIvPath();
        }

        let selectedProduct = input.selectedProduct;
        if (!input.variantId && !selectedProduct) {
            try {
                const resolvedProduct = await getSelectedVariant(
                    new SelectedVariantInput(input.productId, input.channelId, undefined, undefined, ctx.requestContext),
                    ctx
                );

                // A nested variant represents a partial dimension selection. New-IV inventory must
                // wait for a concrete variant instead of surfacing an arbitrary candidate's stock.
                if (resolvedProduct?.productVariant) {
                    ctx.telemetry.debug('[GetProductAvailabilityDispatcher] partial variant selection: skipping New-IV availability');
                    return undefined;
                }

                selectedProduct = resolvedProduct ?? undefined;
            } catch (error) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- telemetry.exception accepts unknown.
                ctx.telemetry.exception(error);
                ctx.telemetry.debug('[GetProductAvailabilityDispatcher] error resolving product for New-IV availability');
                return undefined;
            }
        }

        const isUnresolvedMaster =
            selectedProduct?.ProductTypeValue === ProductType.Master ||
            (selectedProduct?.RecordId === input.productId &&
                !selectedProduct.MasterProductId &&
                ArrayExtensions.hasElements(selectedProduct.Dimensions));
        if (!input.variantId && (!selectedProduct || isUnresolvedMaster)) {
            ctx.telemetry.debug('[GetProductAvailabilityDispatcher] no concrete product resolved for New-IV availability');
            return undefined;
        }

        ctx.telemetry.debug('[GetProductAvailabilityDispatcher] using New-IV path');
        return getDesignatedWarehouseAvailability(
            new ProductAvailabilityDispatcherInput(input.productId, input.channelId, {
                selectedProduct,
                variantId: input.variantId ?? selectedProduct?.RecordId,
                dataAreaIds: input.dataAreaIds,
                orgUnitNumber: input.orgUnitNumber,
                orgUnitName: input.orgUnitName
            }),
            ctx
        );
    }

    ctx.telemetry.debug('[GetProductAvailabilityDispatcher] using Old-IV path');
    return useOldIvPath();
}

export const getProductAvailabilityDispatcherDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-product-availability-dispatcher',
    action: getProductAvailabilityDispatcherAction as IAction<IProductInventoryInformation[] | undefined>,
    input: createProductAvailabilityDispatcherInput
});

export default getProductAvailabilityDispatcherDataAction;
